import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import PublicLayout from "@/components/PublicLayout";
import { api } from "@/lib/api";
import { ArrowRight, Search } from "lucide-react";

export default function Services() {
  const [services, setServices] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("All");

  useEffect(() => {
    api.get("/services").then((r) => setServices(r.data)).catch(() => {});
  }, []);

  const categories = useMemo(() => {
    const s = new Set(services.map((x) => x.category));
    return ["All", ...Array.from(s)];
  }, [services]);

  const filtered = useMemo(() => {
    return services.filter((s) => {
      const okCat = cat === "All" || s.category === cat;
      const okQ = !q || (s.title + " " + s.short_description).toLowerCase().includes(q.toLowerCase());
      return okCat && okQ;
    });
  }, [services, q, cat]);

  return (
    <PublicLayout>
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-8">
        <div className="text-xs uppercase tracking-widest text-blue-700">Our Services</div>
        <h1 className="h-display mt-3 text-5xl md:text-6xl text-slate-900 max-w-4xl">Full-service accounting, tax and business consulting.</h1>
        <p className="mt-5 max-w-2xl text-slate-600 leading-relaxed">
          One senior team for GST, income tax, ROC, payroll, audit and Virtual CFO — so nothing falls through the cracks.
        </p>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-6">
        <div className="flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative flex-1">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <input
              data-testid="services-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search services…"
              className="w-full pl-11 pr-4 py-3 rounded-full border border-slate-200 bg-white text-sm focus:border-blue-500 focus:outline-none"
            />
          </div>
          <div className="flex flex-wrap gap-2">
            {categories.map((c) => (
              <button
                key={c}
                data-testid={`filter-${c.toLowerCase()}`}
                onClick={() => setCat(c)}
                className={`px-4 py-2 rounded-full text-sm border transition-colors ${
                  cat === c
                    ? "bg-slate-900 text-white border-slate-900"
                    : "bg-white text-slate-700 border-slate-200 hover:border-blue-400 hover:text-blue-700"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {filtered.map((s) => (
            <Link
              key={s.id}
              to={`/services/${s.slug}`}
              data-testid={`svc-${s.slug}`}
              className="group block rounded-2xl bg-white border border-slate-200 p-6 hover:border-blue-400 hover:-translate-y-0.5 transition-transform duration-300"
            >
              <div className="text-xs uppercase tracking-widest text-blue-700">{s.category}</div>
              <h3 className="mt-2 text-xl font-medium text-slate-900 group-hover:text-blue-700 transition-colors">{s.title}</h3>
              <p className="mt-3 text-sm text-slate-600 leading-relaxed">{s.short_description}</p>
              <span className="mt-5 inline-flex items-center gap-1 text-sm text-blue-700">Learn more <ArrowRight size={14} /></span>
            </Link>
          ))}
          {filtered.length === 0 && (
            <div className="col-span-full text-center text-slate-500 py-16">No services match your search.</div>
          )}
        </div>
      </section>
    </PublicLayout>
  );
}
